import React, { FC } from 'react';
import OperatorPanelButton from '@components/operatorPanel/components/elements/buttons/OperatorPanelButton';
import { scale as initialScale } from '@components/frame';

import { StyleProp, ViewStyle } from 'react-native';
import { getBoxShadowStyle } from '@styles/shadow';
import { connect } from 'react-redux';
import { ncFileStatuses } from '@helpers';
import { useOperatorPanelStatuses } from '@components/operatorPanel/hooks/useOperatorPanelStatuses';
import { font as initialFont } from '@styles/fonts';
import { FeatureFlagsAvailable } from '@components/featureFlags';
import { PanelProps } from '@components/operatorPanel/components/panel';

interface HomePageTopRightPartProps extends Pick<PanelProps, 'CC' | 'isOperator' | 'scale' | 'font'> {
  isWithERP: boolean | null;
  ncFileStatus: (typeof ncFileStatuses)[keyof typeof ncFileStatuses];
  machineStatus: string;
  featureFlags: Record<string, boolean>;
}

interface ClockInButtonProps extends Pick<HomePageTopRightPartProps, 'CC' | 'scale' | 'font'> {
  boxShadowStyle: StyleProp<ViewStyle>;
  bgColor?: string;
  iconColor?: string;
  textColor?: string;
  borderColor?: string;
}

export const ClockInButton: FC<ClockInButtonProps> = ({
  CC,
  boxShadowStyle,
  bgColor,
  textColor,
  borderColor,
  iconColor,
  scale = initialScale,
  font = initialFont,
}) => {
  return (
    <OperatorPanelButton
      {...{
        CC,
        text: 'Clock In',
        iconConfig: {
          icon: 'userWithClock',
          color: CC(iconColor ?? 'Label/Light/AlwaysDark Primary'),
          height: scale(62),
          width: scale(72),
        },
        textFont: 'Title 1',
        textColor: textColor ?? 'Label/Light/AlwaysDark Primary',
        customButtonBgColor: bgColor ?? 'Label/Dark/AlwaysLight Primary',
        buttonContainerStyle: {
          width: scale(168),
          height: scale(168),
          paddingTop: scale(36),
          paddingBottom: scale(24),
          paddingLeft: scale(34),
          paddingRight: scale(36),
          gap: scale(16),
          borderWidth: 1,
          borderColor: CC(borderColor ?? 'Label/Light/AlwaysLight Primary'),
          borderRadius: scale(18),
          ...boxShadowStyle,
        },
        iconContainerStyle: {
          marginTop: 0,
        },
        scale,
        font,
      }}
    />
  );
};

const HomePageTopRightPartComponent: FC<HomePageTopRightPartProps> = ({
  CC,
  machineStatus,
  isWithERP,
  ncFileStatus,
  featureFlags,
  isOperator = true,
  scale = initialScale,
  font = initialFont,
}) => {
  const { batchLinked, batchNotLinked, machineIsActive, ncFileChange, ncFileIgnored, ncFileLinked, ncFileUnknown } = useOperatorPanelStatuses({
    machineStatus,
    ncFileStatus,
    isOperator,
  });

  const featureFlagForStopButton = FeatureFlagsAvailable('operator-panel-stop-button', featureFlags);

  const shouldDisplayStopButton =
    featureFlagForStopButton &&
    isOperator &&
    ((ncFileLinked && batchLinked && isWithERP && !machineIsActive) ||
      (ncFileLinked && batchNotLinked && !isWithERP && !machineIsActive) ||
      (ncFileUnknown && !machineIsActive) ||
      ncFileIgnored ||
      ncFileChange);

  const shouldDisplayClockInButton = !isOperator;

  if (!shouldDisplayClockInButton && !shouldDisplayStopButton) return null;

  if (shouldDisplayClockInButton) {
    const boxShadowStyle = getBoxShadowStyle({ type: 'Very Wide', scheme: 'light' });

    return <ClockInButton {...{ boxShadowStyle, CC, scale, font }} />;
  }

  if (shouldDisplayStopButton) {
    return (
      <OperatorPanelButton
        {...{
          machineStatus,
          CC,
          text: 'Stop',
          iconConfig: {
            icon: 'square',
            color: CC('Label/Dark/AlwaysLight Primary'),
            height: scale(14),
            width: scale(13),
          },
          buttonContainerStyle: {
            paddingTop: scale(32),
            paddingBottom: scale(32),
            gap: scale(6),
          },
          iconContainerStyle: { marginTop: scale(8), marginBottom: scale(8) },
          scale,
          font,
        }}
      />
    );
  }

  return null;
};

const HomePageTopRightPart = connect(
  ({ membership: { isWithERP }, user: { featureFlags } }) => ({ isWithERP, featureFlags }),
  {}
)(HomePageTopRightPartComponent);

export default HomePageTopRightPart;
